import { createTheme, rem, Combobox, Drawer, Input, Menu, Modal, Popover, Tooltip, } from '@mantine/core';
import { brand, gray, green, orange, red, yellow, purple, radiusScale } from './primitives';
import inputClasses from './inputs.module.css';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const breakpoints = require('./breakpoints.cjs');
// Mantine color tuples are exactly 10 shades, index 0..9. The primitive scales
// expose 50..900 (10 stops), so index 5 === the 500 stop used as the primary shade.
const tuple = (c) => [
    c[50], c[100], c[200], c[300], c[400],
    c[500], c[600], c[700], c[800], c[900],
];
// Brand-tokens-only theme: palette, font, and radii come from the Figma tokens
// (via primitives); everything else keeps Mantine defaults.
export const mantineTheme = createTheme({
    primaryColor: 'brand',
    primaryShade: 5,
    fontFamily: 'Source Sans Pro, sans-serif',
    // Checkbox, Radio, Switch and Chip show the pointer on hover, like every other clickable
    // control. Mantine's default ('default') leaves the arrow, which reads as "not clickable" on
    // exactly the controls whose whole job is to be clicked. One theme setting, so it holds on every
    // page that renders through this theme rather than depending on each call site.
    cursorType: 'pointer',
    // 8px — Layout/radius/md, for controls and surfaces alike. ISN prefers md
    // (2026-09-25): buttons, inputs, selects, badges and tooltips take the same
    // corner as the cards and modals they sit in. This is also Mantine 9's own
    // default, so a stock Mantine example renders here as it does in the docs.
    //
    // Every component takes its radius from here, as in Mantine (`radius:
    // theme.defaultRadius`): no component pins its own. It used to be 'sm' (4px)
    // under a "concentric" rule -- controls tighter than surfaces -- with each
    // surface pinned back to md in `components`; with one radius for both, the pins
    // were only restating this line and are gone. Change it here and everything
    // moves together.
    //
    // A call site that wants something else still passes `radius`. Checkbox keeps
    // Mantine's own radius="sm" defaultProp: at 20px, 8px would read as a radio.
    defaultRadius: 'md',
    // Restated from Layout/radius in tokens.json. xs–lg are byte-identical to
    // Mantine 9's defaults, so this changes nothing today; the point is that the
    // Figma scale is now the declared source rather than a coincidence, and there
    // is one place to change it.
    //
    // xl is deliberately Mantine's 32px and NOT Layout/radius/full (100px): `full`
    // is a pill token, and mapping it here would silently turn the three
    // radius="xl" avatars (MainMenu, OpenCsiModal) into circles. It is exported
    // from primitives as radiusFull for anything that genuinely wants a pill.
    radius: {
        xs: rem(radiusScale.xs),
        sm: rem(radiusScale.sm),
        md: rem(radiusScale.md),
        lg: rem(radiusScale.lg),
        xl: rem(32),
    },
    // Mantine 9's defaults, written down rather than changed. tokens.json has no
    // typography layer at all, so there is nothing to derive these from — and with
    // 149 <Text size="sm"> and 34 size="xs" call sites, moving them would reflow
    // essentially every screen. Declaring them makes the scale visible and gives
    // FastSearch.module.css's var(--mantine-font-size-sm) a stated source.
    fontSizes: {
        xs: rem(12),
        sm: rem(14),
        md: rem(16),
        lg: rem(18),
        xl: rem(20),
    },
    lineHeights: { xs: '1.4', sm: '1.45', md: '1.55', lg: '1.6', xl: '1.65' },
    // Mantine 9's defaults, written down rather than changed -- the same call as
    // fontSizes above, for the same reason. tokens.json's Layout/spacing DOES
    // propose a different scale (xs 4, sm 8, lg 24 -- see spacingScale in
    // primitives), but it was never wired in, so every gap="xs"/p="sm" call site
    // across the app has been rendering Mantine's own numbers the whole time.
    // Adopting the Figma scale now would reflow every one of them; the decision
    // (Chris, 2026-09-02) was to keep what is already shipping and make it the
    // declared source instead of an accident. Revisit spacingScale's comment if
    // that decision changes.
    spacing: { xs: rem(10), sm: rem(12), md: rem(16), lg: rem(20), xl: rem(32) },
    // Same five values postcss-simple-vars substitutes into every *.module.css.
    // See Styles/breakpoints.cjs for why they live in a .cjs file.
    breakpoints,
    colors: {
        brand: tuple(brand),
        gray: tuple(gray),
        green: tuple(green),
        orange: tuple(orange),
        red: tuple(red),
        yellow: tuple(yellow),
        purple: tuple(purple),
    },
    headings: { fontFamily: 'Source Sans Pro, sans-serif' },
    components: {
        // No radius pins: Paper, Card, Alert, SegmentedControl, the overlays and the dropdown panels
        // all take theme.defaultRadius (md), exactly as Mantine does.
        //
        // No InputWrapper override: every input keeps Mantine's own order -- label, description,
        // input, error -- as the docs render it.
        // Input borders in Mantine's own gray[4], since our gray scale's index 4 is darker. A class,
        // not `vars`: an inline --input-bd would sit on the element Mantine's error and success rules
        // target and override them. See Styles/inputs.module.css.
        Input: Input.extend({ classNames: { wrapper: inputClasses.wrapper } }),
        // Mantine's default overlay z-indexes (modal 200, popover 300) sit below
        // the app's sticky headers (MainMenu 1100, OrderHeader 998). Restore the
        // MUI-era scale the shell was designed around: modals/drawers at 1300,
        // floating dropdowns above modal content at 1400, tooltips on top.
        Modal: Modal.extend({ defaultProps: { zIndex: 1300 } }),
        Drawer: Drawer.extend({ defaultProps: { zIndex: 1300 } }),
        Popover: Popover.extend({ defaultProps: { zIndex: 1400 } }),
        Combobox: Combobox.extend({ defaultProps: { zIndex: 1400 } }),
        Menu: Menu.extend({ defaultProps: { zIndex: 1400 } }),
        Tooltip: Tooltip.extend({ defaultProps: { zIndex: 1500 } }),
    },
});
